<!-- eslint-disable vue/multi-word-component-names -->
<!-- eslint-disable vue/valid-v-slot -->

<template>
  <v-app>
    <!-- Top Navigation Bar -->
    <v-app-bar :color="colors.primaryDark" dark elevation="4" height="60">
      <div class="d-flex align-center">
        <v-icon size="40" class="ml-7 mr-2 pulse">fas fa-building</v-icon>
        <span class="text-h6 font-weight-bold gradient-text">
          SICON PAINEL DE CONTROLE I.A
        </span>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center" style="gap: 24px">
        <!-- User Profile -->
        <div class="d-flex align-center mr-12 user-profile" style="gap: 7px">
          <v-avatar size="40" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="20">fas fa-user</v-icon>
          </v-avatar>
          <div class="d-flex flex-column">
            <span class="font-weight-bold text-body-2">
              {{ userDetails?.name || 'Carregando...' }}
            </span>
            <span class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Online' }}
            </span>
          </div>
        </div>
      </div>
    </v-app-bar>

    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      :color="colors.primaryDark"
      dark
      permanent
      width="250"
      class="custom-drawer"
    >
      <!-- User Profile in Sidebar -->
      <div class="pa-4 sidebar-profile" style="border-bottom: 1px solid #1a4b7a">
        <div class="d-flex align-center" style="gap: 12px">
          <v-avatar size="45" :color="colors.primaryLight" class="profile-avatar">
            <v-icon color="white" size="22">fas fa-user</v-icon>
          </v-avatar>
          <div>
            <div class="font-weight-bold text-body-2 lowercase">
              {{ userDetails?.name || 'Carregando...' }}
            </div>
            <div class="text-caption opacity-75">
              {{ userDetails?.department?.name || 'Sistema Online' }}
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Menu -->
      <v-list nav class="pa-0 text-start">
        <v-list-item
          v-for="item in menuItems"
          :key="item.title"
          :value="item.value"
          :active="isActiveRoute(item)"
          rounded="xl"
          class="mb-2 menu-item"
          :class="isActiveRoute(item) ? 'active-item' : ''"
          lines="one"
          density="comfortable"
          @click="navigateToPage(item)"
        >
          <template #prepend>
            <v-icon size="22" class="mr-2">{{ item.icon }}</v-icon>
          </template>

          <template #title>
            <span class="item-title">{{ item.title }}</span>
          </template>

          <template #append v-if="item.badge">
            <v-chip size="small" :color="item.badgeColor" class="pulse-badge">
              {{ item.badge }}
            </v-chip>
          </template>
        </v-list-item>
      </v-list>

      <template v-slot:append>
        <div class="pa-3" style="border-top: 1px solid #1a4b7a">
          <v-btn
            block
            variant="outlined"
            prepend-icon="fas fa-sign-out-alt"
            class="logout-btn"
            @click="logout"
          >
            Sair
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main class="bg-grey-lighten-5 main-content" style="margin-top: 20px">
      <!-- Tickets Page Content -->
      <div class="tickets-page">
        <!-- Header Section -->
        <div class="page-header">
          <div class="d-flex align-center justify-space-between">
            <div class="d-flex align-center" style="gap: 16px">
              <v-icon size="40" :color="colors.primaryLight" class="pulse">
                fas fa-ticket-alt
              </v-icon>
              <div>
                <h1 class="text-h4 font-weight-bold gradient-text mb-0">
                  Gerenciamento de Tickets
                </h1>
                <p class="text-subtitle-1 text-grey-darken-2 mb-0">
                  Visualize e gerencie todos os tickets do sistema
                </p>
              </div>
            </div>
            
            <div class="d-flex align-center" style="gap: 12px">
              <!-- Total de Tickets Card -->
              <v-card 
                :color="colors.primaryLight" 
                dark 
                class="tickets-counter"
                elevation="4"
              >
                <v-card-text class="pa-3">
                  <div class="d-flex align-center" style="gap: 8px">
                    <v-icon size="24">fas fa-list-alt</v-icon>
                    <div>
                      <div class="text-caption opacity-75">Total</div>
                      <div class="text-h6 font-weight-bold">{{ tickets.length }}</div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <!-- Refresh Button -->
              <v-btn
                :color="colors.primaryGreen"
                variant="elevated"
                :loading="loading"
                @click="fetchTickets"
                class="action-btn"
              >
                <v-icon left>fas fa-sync-alt</v-icon>
                Atualizar
              </v-btn>

              <!-- Delete All Button -->
              <v-btn
                color="error"
                variant="elevated"
                :disabled="tickets.length === 0 || deleting"
                :loading="deleting"
                @click="showDeleteDialog = true"
                class="action-btn"
              >
                <v-icon left>fas fa-trash-alt</v-icon>
                Apagar Todos
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Filters Section -->
        <v-card class="mb-6 filter-card" elevation="2">
          <v-card-text>
            <div class="d-flex align-center" style="gap: 16px">
              <v-text-field
                v-model="searchQuery"
                prepend-inner-icon="fas fa-search"
                label="Buscar tickets..."
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="search-field"
              ></v-text-field>
              
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                label="Status"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                class="status-filter"
              ></v-select>
            </div>
          </v-card-text>
        </v-card>

        <!-- Tickets List -->
        <v-card elevation="3" class="tickets-card">
          <v-card-title class="pa-4" :style="{ backgroundColor: colors.primaryDark, color: 'white' }">
            <v-icon class="mr-2">fas fa-list</v-icon>
            Lista de Tickets
          </v-card-title>

          <v-card-text class="pa-0">
            <!-- Loading State -->
            <div v-if="loading" class="text-center pa-8">
              <v-progress-circular 
                :color="colors.primaryLight" 
                size="64" 
                indeterminate
              ></v-progress-circular>
              <p class="text-subtitle-1 mt-4">Carregando tickets...</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="filteredTickets.length === 0" class="text-center pa-8">
              <v-icon size="64" :color="colors.primaryLight" class="mb-4">
                fas fa-inbox
              </v-icon>
              <h3 class="text-h6 mb-2">Nenhum ticket encontrado</h3>
              <p class="text-subtitle-1 text-grey-darken-2">
                {{ tickets.length === 0 ? 'Não há tickets cadastrados no sistema.' : 'Nenhum ticket corresponde aos filtros aplicados.' }}
              </p>
            </div>

            <!-- Tickets Table -->
            <div v-else>
              <v-data-table
                :headers="headers"
                :items="filteredTickets"
                :items-per-page="10"
                class="tickets-table"
                no-data-text="Nenhum ticket encontrado"
                loading-text="Carregando tickets..."
              >
                <!-- ID Column -->
                <template v-slot:item.id="{ item }">
                  <v-chip 
                    :color="colors.primaryDark" 
                    text-color="white" 
                    size="small"
                    class="font-weight-bold"
                  >
                    #{{ item.id }}
                  </v-chip>
                </template>

                <!-- Title Column -->
                <template v-slot:item.titulo="{ item }">
                  <div class="ticket-title">
                    <div class="font-weight-medium">{{ item.titulo }}</div>
                    <div class="text-caption text-grey-darken-1">
                      Criado em {{ formatDate(item.created) }}
                    </div>
                  </div>
                </template>

                <!-- Status Column -->
                <template v-slot:item.status="{ item }">
                  <v-chip 
                    :color="getStatusColor(item.status)"
                    text-color="white"
                    size="small"
                  >
                    {{ item.status }}
                  </v-chip>
                </template>

                <!-- Priority Column -->
                <template v-slot:item.priority="{ item }">
                  <v-chip 
                    :color="getPriorityColor(item.priority)"
                    text-color="white"
                    size="small"
                    variant="flat"
                  >
                    {{ item.priority }}
                  </v-chip>
                </template>

                <!-- Actions Column -->
                <template v-slot:item.actions="{ item }">
                  <div class="d-flex" style="gap: 8px">
                    <v-btn
                      :color="colors.primaryLight"
                      variant="text"
                      size="small"
                      icon
                      @click="viewTicket(item)"
                    >
                      <v-icon size="16">fas fa-eye</v-icon>
                    </v-btn>
                    <v-btn
                      color="error"
                      variant="text"
                      size="small"
                      icon
                      @click="deleteTicket(item)"
                    >
                      <v-icon size="16">fas fa-trash</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>

        <!-- Delete All Confirmation Dialog -->
        <v-dialog v-model="showDeleteDialog" max-width="500">
          <v-card>
            <v-card-title class="text-h5 text-error">
              <v-icon class="mr-2" color="error">fas fa-exclamation-triangle</v-icon>
              Confirmar Exclusão
            </v-card-title>
            <v-card-text>
              <p class="text-body-1">
                Tem certeza de que deseja apagar <strong>todos os {{ tickets.length }} tickets</strong>?
              </p>
              <p class="text-body-2 text-error">
                Esta ação não pode ser desfeita!
              </p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn 
                text 
                @click="showDeleteDialog = false"
                :disabled="deleting"
              >
                Cancelar
              </v-btn>
              <v-btn 
                color="error" 
                variant="flat"
                :loading="deleting"
                @click="deleteAllTickets"
              >
                Apagar Todos
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Snackbar for notifications -->
        <v-snackbar
          v-model="snackbar.show"
          :color="snackbar.color"
          :timeout="snackbar.timeout"
          location="top right"
        >
          {{ snackbar.message }}
          <template v-slot:actions>
            <v-btn
              color="white"
              variant="text"
              @click="snackbar.show = false"
            >
              Fechar
            </v-btn>
          </template>
        </v-snackbar>
      </div>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, onMounted, computed, defineEmits, watch } from "vue";
import { menuItems } from '@/constants/MenuItems'
import { useRouter, useRoute } from 'vue-router';
import axios from "axios";
import { useAuthStore } from '@/stores/auth'

const API_BASE = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');

// Router setup
const router = useRouter();
const route = useRoute();

// State - Layout
const drawer = ref(true);
const userDetails = ref(null);

// State - Tickets
const tickets = ref([]);
const loading = ref(false);
const deleting = ref(false);
const showDeleteDialog = ref(false);
const searchQuery = ref('');
const statusFilter = ref(null);
const authStore = useAuthStore();
const auth = useAuthStore();
const token = auth?.token || null;


// Snackbar state
const snackbar = ref({
  show: false,
  message: '',
  color: 'success',
  timeout: 3000
});

// Colors (matching the layout)
const colors = {
  primaryDark: "#123A5D",
  primaryLight: "#57C2E3",
  primaryGreen: "#A3C940",
  lightBg: "#E7F2F9",
};

// Table headers
const headers = [
  { title: 'ID', key: 'id', sortable: true, width: '100px' },
  { title: 'Título', key: 'titulo', sortable: true },
  { title: 'Status', key: 'status', sortable: true, width: '120px' },
  { title: 'Prioridade', key: 'priority', sortable: true, width: '120px' },
  { title: 'Ações', key: 'actions', sortable: false, width: '100px' }
];

// Status options for filter
const statusOptions = [
  'Aberto',
  'Em Andamento',
  'Aguardando',
  'Resolvido',
  'Fechado'
];

// Mock data (will be replaced by API data)
const mockTickets = [
  {
    id: 1,
    titulo: "Sistema de login não está funcionando",
    created: new Date('2024-01-15T10:30:00'),
    status: "Aberto",
    priority: "Alta"
  }
];

// Computed
const filteredTickets = computed(() => {
  let filtered = [...tickets.value];
  
  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(ticket => 
      ticket.titulo.toLowerCase().includes(query) ||
      ticket.id.toString().includes(query)
    );
  }
  
  // Filter by status
  if (statusFilter.value) {
    filtered = filtered.filter(ticket => ticket.status === statusFilter.value);
  }
  
  return filtered;
});

// Methods - Navigation
const navigateToPage = (item) => {
  try {
    // Se o item tem uma rota específica, navegar para ela
    if (item.route) {
      router.push(item.route);
    } else if (item.path) {
      router.push(item.path);
    } else {
      // Fallback para navegar baseado no valor do item
      const routeName = item.value;
      router.push({ name: routeName });
    }
    
    // Emitir evento para componentes pai
    emit('menu-item-click', item);
    
    // Feedback visual
    showSnackbar(`Navegando para ${item.title}`, 'info');
    
  } catch (error) {
    console.error('Erro na navegação:', error);
    showSnackbar(`Erro ao navegar para ${item.title}. Verifique se a rota existe.`, 'error');
  }
};

const isActiveRoute = (item) => {
  // Verificar se a rota atual corresponde ao item do menu
  const currentRouteName = route.name;
  const currentRoutePath = route.path;
  
  // Verificar por nome da rota
  if (item.route && typeof item.route === 'object' && item.route.name) {
    return currentRouteName === item.route.name;
  }
  
  // Verificar por path
  if (item.route && typeof item.route === 'string') {
    return currentRoutePath === item.route;
  }
  
  if (item.path) {
    return currentRoutePath === item.path;
  }
  
  // Verificar por valor (fallback)
  if (item.value) {
    return currentRouteName === item.value || currentRoutePath.includes(item.value);
  }
  
  return false;
};

// Emit setup
const emit = defineEmits(['menu-item-click', 'user-loaded']);

// Methods - User
const fetchUserDetails = async () => {
  if (!token) return;

  try {
    const response = await axios.get(`${API_BASE}/me`, { 
      headers: { Authorization: `Bearer ${token}` } 
    });

    if (response.data) {
      userDetails.value = response.data;
      emit('user-loaded', response.data);
    }
  } catch (error) {
    console.error('Erro ao buscar dados do usuário:', error);
    userDetails.value = null;
  }
};

const logout = () => {
  if (authStore && typeof authStore.logout === 'function') {
    authStore.logout();
  }
  if (router && typeof router.push === 'function') {
    router.push('/');
  }
};

// Methods - Tickets
const fetchTickets = async () => {
  loading.value = true;
  try {
    const response = await axios.get(`${API_BASE}/notification`);
    // Mapear os campos da API para os campos esperados pelo componente
    tickets.value = response.data.map(notification => ({
      id: notification.id,
      titulo: notification.title || 'Sem título',
      created: notification.createdAt,
      status: "Aberto", // Valor padrão já que não vem da API
      priority: "Média" // Valor padrão já que não vem da API
    }));
    showSnackbar('Notificações carregadas com sucesso!', 'success');
  } catch (error) {
    console.error('Erro ao buscar notificações:', error);
    tickets.value = mockTickets;
    showSnackbar('Erro ao conectar com a API. Usando dados de exemplo.', 'warning');
  } finally {
    loading.value = false;
  }
};

const deleteAllTickets = async () => {
  deleting.value = true;
  try {
    // Apagar cada notificação individualmente
    await Promise.all(
      tickets.value.map(ticket => 
        axios.delete(`${API_BASE}/notification/${ticket.id}`)
      )
    );
    tickets.value = [];
    showDeleteDialog.value = false;
    showSnackbar('Todas as notificações foram apagadas com sucesso!', 'success');
  } catch (error) {
    console.error('Erro ao apagar notificações:', error);
    showSnackbar('Erro ao apagar notificações. Tente novamente.', 'error');
  } finally {
    deleting.value = false;
  }
};

const deleteTicket = async (ticket) => {
  try {
    await axios.delete(`${API_BASE}/notification/${ticket.id}`);
    tickets.value = tickets.value.filter(t => t.id !== ticket.id);
    showSnackbar('Notificação apagada com sucesso!', 'success');
  } catch (error) {
    console.error('Erro ao apagar notificação:', error);
    showSnackbar('Erro ao apagar notificação. Tente novamente.', 'error');
  }
};

const viewTicket = (ticket) => {
  showSnackbar(`Visualizando ticket #${ticket.id}`, 'info');
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusColor = (status) => {
  const colors = {
    'Aberto': 'blue',
    'Em Andamento': 'orange',
    'Aguardando': 'purple',
    'Resolvido': 'green',
    'Fechado': 'grey'
  };
  return colors[status] || 'grey';
};

const getPriorityColor = (priority) => {
  const colors = {
    'Baixa': 'green',
    'Média': 'orange',
    'Alta': 'red',
    'Crítica': 'error'
  };
  return colors[priority] || 'grey';
};

const showSnackbar = (message, color = 'success') => {
  snackbar.value = {
    show: true,
    message,
    color,
    timeout: 3000
  };
};

// Watch for route changes to update active state
watch(() => route.path, () => {
  // Força a reatividade quando a rota muda
}, { immediate: true });

// Lifecycle
onMounted(() => {
  fetchUserDetails();
  fetchTickets();
});
</script>

<style scoped>
/* Animations */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

.pulse {
  animation: pulse 2s infinite;
}

/* Layout Styles */
.gradient-text {
  background: linear-gradient(45deg, #57C2E3, #A3C940);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
}

.user-profile {
  transition: all 0.3s ease;
  border-radius: 12px;
  padding: 8px 12px;
}

.user-profile:hover {
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
}

.profile-avatar {
  border: 2px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
}

.profile-avatar:hover {
  border-color: rgba(255, 255, 255, 0.6);
  transform: scale(1.05);
}

.custom-drawer {
  background: linear-gradient(180deg, #123A5D 0%, #0f2a42 100%);
}

.menu-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 4px 0;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  transform: translateX(4px);
}

.active-item {
  background: linear-gradient(135deg, #57C2E3, #4aa3c1) !important;
  box-shadow: 0 4px 12px rgba(87, 194, 227, 0.3);
}

.active-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background: #A3C940;
}

.pulse-badge {
  animation: pulse 2s infinite;
}

.logout-btn {
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.8);
  transition: all 0.3s ease;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.6);
}

.main-content {
  background: linear-gradient(135deg, #f5f5f5 0%, #e8f4f8 100%);
}

.lowercase {
  text-transform: lowercase;
}

/* Tickets Page Styles */
.tickets-page {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 32px;
  padding: 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%);
  border-radius: 16px;
  border-left: 5px solid #57C2E3;
}

.tickets-counter {
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.tickets-counter:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(87, 194, 227, 0.3);
}

.action-btn {
  border-radius: 12px;
  transition: all 0.3s ease;
  text-transform: none;
  font-weight: 600;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.filter-card {
  border-radius: 16px;
  border-top: 4px solid #57C2E3;
}

.tickets-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.search-field {
  flex: 1;
  max-width: 400px;
}

.status-filter {
  width: 200px;
}

.ticket-title {
  line-height: 1.4;
}

.tickets-table {
  background: transparent;
}

.tickets-table :deep(.v-data-table-header) {
  background-color: #f5f5f5;
}

.tickets-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(87, 194, 227, 0.05) !important;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .tickets-page {
    padding: 16px;
  }
  
  .page-header .d-flex {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
}

@media (max-width: 960px) {
  .user-profile span:last-child {
    display: none;
  }
}

@media (max-width: 768px) {
  .page-header {
    padding: 16px;
  }
  
  .filter-card .d-flex {
    flex-direction: column;
    gap: 12px;
  }
  
  .search-field,
  .status-filter {
    width: 100%;
    max-width: none;
  }
  
  .tickets-counter {
    width: 100%;
  }
}

@media (max-width: 600px) {
  .gradient-text {
    font-size: 1rem;
  }
}
</style>
